import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { setSentryUser } from '../lib/sentry.js'

// Tracks the current Supabase Auth session.
//
// Two states matter to the rest of the app:
//   loading — true on initial mount while we ask Supabase what
//             session (if any) is in localStorage. Briefly true,
//             but the App shell uses it to avoid flickering the
//             login screen for an already-logged-in user.
//   session — null when nobody is logged in; the auth session
//             object when somebody is. session.user.id matches
//             auth.users.id (and therefore restaurant_members.user_id).
//
// Listens for auth state changes (sign in, sign out, token refresh)
// so the UI updates without manual refreshes.
export function useSession() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  // Track the last auth event so callers can react to specific
  // transitions — particularly PASSWORD_RECOVERY, which fires when
  // a manager arrives via a password-reset email link. The App
  // shell uses that to route to PasswordResetScreen instead of
  // dropping the user into the regular tab UI.
  const [authEvent, setAuthEvent] = useState(null)

  useEffect(() => {
    let mounted = true

    // Initial check — Supabase JS persists the session in localStorage
    // by default, so a returning user already has a valid session here.
    supabase.auth.getSession().then(({ data, error }) => {
      if (!mounted) return
      if (error) {
        // eslint-disable-next-line no-console
        console.error('[useSession] getSession failed', error)
      }
      setSession(data?.session ?? null)
      setSentryUser(data?.session?.user ?? null)
      setLoading(false)
    })

    // Subscribe for future state changes — covers sign in, sign out,
    // token refresh, password recovery, etc. Supabase fires this
    // with the new session (or null on sign out).
    const { data: subscription } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!mounted) return
        setSession(newSession)
        setAuthEvent(event)
        setSentryUser(newSession?.user ?? null)
      }
    )

    return () => {
      mounted = false
      subscription?.subscription?.unsubscribe()
    }
  }, [])

  // clearAuthEvent lets a screen acknowledge it's handled the event
  // (e.g. PasswordResetScreen finished and the manager re-signed in).
  function clearAuthEvent() {
    setAuthEvent(null)
  }

  return {
    session,
    loading,
    user: session?.user ?? null,
    authEvent,
    clearAuthEvent,
  }
}
